<script setup lang="ts">
    // @ts-nocheck
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    import { useValidation } from "@/components/forms/reusables/useValidation";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import KSuffix from "@/components/forms/subComponents/Suffix/KSuffix.vue";
    import { useStore } from "@/store";
    // noinspection ES6UnusedImports (det här är för Jetbrainsanvändare)
    import { getEventColor } from "@/tabs/Operation/colors-utils";
    import { computed, defineEmits, inject, onBeforeMount, ref, toRefs, watch } from "vue";

    const store = useStore();

    // TODO Använd CommonProps i types i Vue3
    interface Props {
        validation?: string | Array<[rule: string, ...args: never]>;
        name: string;
        disabled?: boolean;
        type?: never;
        label?: string;
        /**
         * Visa toggle checkbox. Används för att få "välj all alternativ"-funktionalitet
         */
        allowToggle?: boolean;
        /**
         * Label på toggle checkboxen
         */
        toggleLabel?: string;
        /**
         * Options som ska visas från början. Det kan antingen vara en array som INTE sätts async eller async funktion
         * som returnerar array med options. Kommer anropas vid första rendering
         */
        options?: Array<{ label: string; value: string }> | (() => Promise<Array<{ label: string; value: string }>>);
        /**
         * Konvertering som görs submit return. complex gör ingen konvertering och kan användas för t.ex objekt.
         * Används med options, när fler checkboxes renderas
         */
        valueType?: "boolean" | "number" | "string" | "complex";
        /**
         * Riknting som en grupp av checkboxes ska renderas
         */
        direction?: "horizontal" | "vertical";
        /**
         * Ska färgsättning av varje checkbox göras via id? Om det är satt till true kommer även toggle göra att
         * options visas eller döljs
         */
        useColorKey?: boolean;
        /**
         * Funktion som returnerar förändrade värdet. Används vid submit.
         * Körs istället för konvertering via valueType, om båda är satta
         */
        converter?: ({ value }: { value: unknown[] | boolean }) => unknown[] | unknown;
        /**
         * Gör så att om värdet i fältet är tomt (tom sträng, objekt, sträng) eller falsy (Gäller EJ nummer (siffra 0))
         * så kommer det rensas i formulärdatan vid submit.
         */
        ignoreEmptyData?: boolean;
        /**
         * Ska rendering av fält horisontellt ignorera kolumnerna i Kforms grid?
         */
        freeFlow?: boolean;
        /**
         * Ska värdet på fältet tas bort vid submit? Kan användas för att ha checkboxes med triggers i formuläret
         * utan att skräpa ner submit med data
         */
        ignoreValue?: boolean;
        /**
         * Human readable-namn som ska visas vid error
         */
        validationName?: string;
        defaultValue?: unknown;
        valign?: "default" | "center";
        /**
         * KSuffix.vue prop Text som renderas under input
         */
        suffixText?: string;
        /**
         * KSuffix.vue prop Ikon som renderas under input
         */
        suffixIcon?: string;
        /**
         * KSuffix.vue prop Typ av subtitle
         */
        suffixType?: undefined | "none" | "info" | "info-dark";
        /**
         * Olika checkbox varianter, stöd för default och complete
         */
        variant?: "default" | "complete";
        value?: unknown;
    }

    const props = withDefaults(defineProps<Props>(), {
        disabled: false,
        allowToggle: false,
        valueType: "complex",
        direction: "vertical",
        ignoreFalsyData: true,
        freeFlow: false,
        ignoreValue: false,
        valign: "default",
    });

    const {
        name,
        disabled,
        allowToggle,
        options,
        valueType,
        converter,
        ignoreEmptyData,
        ignoreValue,
        validation,
        defaultValue,
        valign,
        suffixText,
        variant,
        value,
    } = toRefs(props);

    const fetchedOptions = ref<Props["options"] | undefined>();
    const modifiedDisabled = ref(disabled?.value);
    const inputRef = ref();
    // Klättrar upp till KForm (FormulateInput (inputRef) > KCheckbox > FormulateForm > KForm
    const formRef = computed(() => inputRef?.value?.$parent?.$parent?.$parent);
    const inputValue = ref(
        (() => {
            if (value?.value) {
                return value.value;
            }

            if (options?.value) {
                return defaultValue?.value ?? [];
            }

            if (valueType.value === "boolean") {
                return defaultValue?.value ?? false;
            }

            return defaultValue?.value;
        })()
    );
    const toggleValue = ref(false);

    const modifiedSuffix = ref(suffixText?.value);
    const setSuffix = ({ value }: { value: string | null }) => {
        modifiedSuffix.value = value;
    };

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    } | null>("configNames", null);

    const { modifiedValidation, expose: useValidationExpose } = useValidation({
        name: name.value,
        validation: validation?.value,
        moduleName: configNames?.moduleName,
        subModuleName: configNames?.subModuleName,
    });

    const setDisabled = ({ value }: { value: boolean }) => {
        modifiedDisabled.value = value;
    };

    defineExpose({
        setDisabled,
        /*
            Gör så att om värdet i fältet är tomt (tom sträng, objekt, sträng)
            så kommer det rensas formulärdatan vid submit.
         */
        ignoreEmptyData: () => ignoreEmptyData?.value,
        convertData: converter?.value,
        /** Används av convertFormData i KForm för att avgöra hur datan ska konverteras innan det skickas till BE */
        kType: "checkbox",
        /** Används av convertFormData i Kform för att avgöra hur värdet i checkbox ska konverteras */
        kValueType: () => valueType.value,
        ignoreValue: () => ignoreValue.value,
        ...useValidationExpose,
        setSuffix,
    });

    const shouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            ...(configNames ?? {}),
            name: name.value,
        })
    );

    const getColorId = ({ index, value }: { index: number; value: unknown }) => {
        if (isNaN(Number(value))) {
            return index + 1;
        }

        if (Number(value) === 0) {
            return 0;
        }

        return Number(value);
    };

    const emit = defineEmits<{
        /**
         * ANVÄND BARA DET HÄR NÄR KCheckbox ANVÄNDS UTANFÖR FORMULÄR.
         * Använd KForm listeners/triggers i andra fall
         */
        (event: "inputValue", payload: { value: unknown }): void;
    }>();

    onBeforeMount(async () => {
        if (options?.value) {
            if (Array.isArray(options.value)) {
                fetchedOptions.value = options.value;
            } else {
                try {
                    fetchedOptions.value = await options.value?.();
                } catch (error) {
                    console.error(error);
                }
            }

            // Funktionalitet som används om flera checkboxes ska kunna togglas på/av
            if (allowToggle?.value) {
                watch(
                    [inputValue, fetchedOptions],
                    ([newInputValue, newFetchedOptions], [oldInputValue, oldFetchedOptions]) => {
                        if (newFetchedOptions?.length) {
                            // Om något value är satt. Då sätter vi toggle till false
                            if (toggleValue.value && newInputValue?.length !== newFetchedOptions.length) {
                                toggleValue.value = false;
                                // Om toggle är false och alla options valt. Då sätter vi det till true
                            } else if (!toggleValue.value && newInputValue?.length === newFetchedOptions.length) {
                                toggleValue.value = true;
                            }
                        }
                    }
                );

                watch([toggleValue, fetchedOptions], ([newToggleValue, newFetchedOptions], [oldToggleValue]) => {
                    if (newFetchedOptions?.length) {
                        // Om toggle har togglat från false till true
                        if (newToggleValue && !oldToggleValue) {
                            // Om det inte finns några option valda eller om alla options inte är valda
                            if (!inputValue?.value?.length || inputValue?.value?.length !== newFetchedOptions.length) {
                                formRef?.value?.setFieldValue({
                                    name: name.value,
                                    value: newFetchedOptions.map((option) => option.value),
                                });
                            }
                            /*
                                Om toggle har togglat från true till false och det finns satta options eller alla
                                options är satta
                             */
                        } else if (
                            !newToggleValue &&
                            oldToggleValue &&
                            (!inputValue?.value?.length || inputValue?.value?.length == newFetchedOptions.length)
                        ) {
                            formRef?.value?.setFieldValue({
                                name: name.value,
                                value: [],
                            });
                        }
                    }
                });
            }
        }
    });

    watch(inputValue, (newValue) => {
        emit("inputValue", { value: newValue });
    });

    watch(value, (newValue) => {
        inputValue.value = newValue;
    });
</script>
<template>
    <div
        v-if="shouldBeVisible"
        :class="[
            'k-checkbox-wrapper',
            `valign-${valign}`,
            {
                'toggled-off': allowToggle && !toggleValue && !inputValue?.length,
                'free-flow': freeFlow,
            },
        ]"
    >
        <FormulateInput
            v-if="allowToggle"
            v-model="toggleValue"
            :name="`${name}-k-checkbox-toggle`"
            :id="`${name}-k-checkbox-toggle`"
            type="checkbox"
            class="k-checkbox toggle"
            :disabled="modifiedDisabled"
            :validationName="validationName ?? label"
        >
            <template #label>
                <KLabel
                    v-if="toggleLabel"
                    :validation="modifiedValidation"
                    :content="toggleLabel"
                    margin="none"
                    :input-name="`${name}-k-checkbox-toggle`"
                />
            </template>
            <template #help>
                <KSuffix class="span-all" :subText="suffixText" :subIcon="suffixIcon" :subType="suffixType" />
            </template>
        </FormulateInput>
        <FormulateInput
            ref="inputRef"
            v-bind="{ ...$attrs, ...$props, ...$slots }"
            v-model="inputValue"
            :name="name"
            :id="name"
            type="checkbox"
            :class="[`k-checkbox ${variant}`, direction, { 'free-flow': freeFlow }]"
            :disabled="modifiedDisabled"
            :options="fetchedOptions"
            :validation="modifiedValidation"
            :defaultValue="defaultValue"
        >
            <template #label>
                <KLabel
                    v-if="label"
                    :validation="modifiedValidation"
                    :content="label"
                    margin="none"
                    :input-name="name"
                />
            </template>
            <template v-if="fetchedOptions" #element="{ options }">
                <div
                    :class="[
                        'formulate-input-group',
                        { 'toggled-off': allowToggle && !toggleValue && !inputValue?.length },
                    ]"
                >
                    <FormulateInput
                        v-for="(option, index) in options"
                        v-model="inputValue"
                        type="checkbox"
                        :key="index"
                        :value="option.value"
                        :id="`${name}-${index}`"
                        class="k-checkbox"
                        :style="{
                            '--backgroundColor': useColorKey
                                ? getEventColor(getColorId({ index, value: option.value })).backgroundColor
                                : 'default',
                        }"
                        ignored
                        :disabled="modifiedDisabled"
                    >
                        <template #label>
                            <KLabel
                                v-if="option.label"
                                :content="option.label"
                                margin="none"
                                :input-name="`${name}-${index}`"
                            />
                        </template>
                    </FormulateInput>
                </div>
            </template>

            <template #help>
                <KSuffix class="span-all" :subText="modifiedSuffix" :subIcon="suffixIcon" :subType="suffixType" />
            </template>
        </FormulateInput>
    </div>
</template>

<style scoped lang="scss">
    @use "@/components/forms/Form/styles/FormContent";
    @import "@/style/form";
    @import "@/style/variables";

    .k-checkbox-wrapper {
        display: grid;
        height: fit-content;

        &.valign-center {
            margin-top: calc($form-label-default-line-height + $form-label-default-margin-bottom);
        }

        &:not(.toggled-off) {
            grid-gap: $form-row-gap / 2;
        }

        .k-checkbox {
            display: grid;
            align-items: center;
            margin: 0;

            &.complete {
                background: $color-background-tool-menu;
                border-radius: 4px;
                border: 1px $color-tablerow-button-selected solid;
                width: fit-content;
                padding: 1rem;

                ::v-deep .formulate-input-wrapper {
                    .formulate-input-element--checkbox {
                        &:has(input:checked) {
                            label {
                                background: url("@/assets/checkmark_light.svg") 50% no-repeat;
                                background-color: $color-checkbox-complete;
                            }
                        }
                    }
                }
            }

            &.toggle {
                ::v-deep .formulate-input-wrapper label {
                    font-family: Roboto Medium, sans-serif;
                    font-weight: bold;
                    color: inherit;
                }
            }

            ::v-deep .formulate-input-wrapper {
                gap: unset;
                display: grid;
                grid-gap: 0.5rem;
                grid-auto-flow: column;
                align-items: center;
                grid-template-columns: max-content 1fr;

                &:has(> .formulate-input-group) {
                    grid-auto-flow: row;
                    grid-template-columns: unset;
                    grid-gap: unset;
                }

                .formulate-input-element--checkbox {
                    display: grid;

                    label {
                        top: 0;
                        border-radius: 6px;
                        width: 1.3rem;
                        height: 1.3rem;
                    }

                    &:has(input:checked) {
                        label {
                            background: url("@/assets/checkmark_darker.svg") 50% no-repeat;
                            background-color: var(--backgroundColor);
                        }
                    }
                }
            }

            ::v-deep .formulate-input-group {
                display: grid;
                grid-gap: $form-row-gap / 2;
                align-items: center;

                &.toggled-off {
                    display: none;
                }

                .formulate-input-group-item {
                    margin: 0;

                    .formulate-input-label {
                        margin-bottom: 0;
                    }
                }
            }

            &.vertical {
                ::v-deep .formulate-input-group {
                    grid-auto-flow: row;
                }
            }

            &.horizontal {
                &.free-flow {
                    ::v-deep .formulate-input-group {
                        display: flex;
                        flex-direction: row;
                        flex-wrap: wrap;
                        column-gap: FormContent.$k-form-column-gap;
                    }
                }

                :not(.free-flow) {
                    ::v-deep .formulate-input-group {
                        grid-auto-flow: column;
                    }
                }
            }
        }
    }
</style>
